import { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import s from "./styles.module.scss";
import chevronDownIcon from "../../assets/chewron_down.svg";
import config from "../../config";

/**
 * SchoolDropdown - A searchable dropdown for selecting schools
 *
 * Props:
 * - value: { school_id: number | null, school_name: string } | null
 * - onChange: (selection: { school_id: number | null, school_name: string, custom_school?: string }) => void
 * - placeholder: string
 * - hasError: boolean
 * - disabled: boolean
 * - required: boolean
 */
const SchoolDropdown = ({
  value = null,
  onChange,
  placeholder = "Search for your school...",
  hasError = false,
  disabled = false,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customSchoolName, setCustomSchoolName] = useState("");

  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const debounceTimeout = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownListRef.current &&
        !dropdownListRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Position the dropdown menu
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Search schools with debounce
  const searchSchools = useCallback(async (query) => {
    if (query.length < 2) {
      setSchools([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/schools/search?q=${encodeURIComponent(query)}`
      );
      const data = await response.json();

      if (data.status === "success") {
        setSchools(data.data || []);
      }
    } catch (error) {
      console.error("Error searching schools:", error);
      setSchools([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowCustomInput(false);

    // Clear existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Debounce the search
    debounceTimeout.current = setTimeout(() => {
      searchSchools(query);
    }, 300);
  };

  // Handle school selection
  const handleSelect = (school) => {
    if (school.name === "Other") {
      setShowCustomInput(true);
      setSearchQuery("");
      setSchools([]);
    } else {
      onChange({
        school_id: school.id,
        school_name: school.name,
      });
      setIsOpen(false);
      setSearchQuery("");
      setShowCustomInput(false);
    }
  };

  // Handle custom school submission
  const handleCustomSchoolSubmit = () => {
    if (customSchoolName.trim()) {
      onChange({
        school_id: null,
        school_name: "Other",
        custom_school: customSchoolName.trim(),
      });
      setIsOpen(false);
      setShowCustomInput(false);
      setCustomSchoolName("");
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
      if (!isOpen) {
        setSearchQuery("");
        setSchools([]);
        setShowCustomInput(false);
      }
    }
  };

  // Display value
  const displayValue = value?.custom_school
    ? `Other: ${value.custom_school}`
    : value?.school_name || placeholder;

  const rootClass = [
    s.dropdown,
    isOpen && s.dropdownOpen,
    hasError && s.dropdownError,
    disabled && s.dropdownDisabled,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div ref={dropdownRef} className={rootClass}>
        <button
          ref={triggerRef}
          type="button"
          className={s.dropdownTrigger}
          onClick={toggleDropdown}
          aria-expanded={isOpen}
          disabled={disabled}
        >
          <span
            className={`${s.dropdownValue} ${
              !value?.school_name ? s.dropdownPlaceholder : ""
            }`}
          >
            {displayValue}
          </span>
          <span className={s.dropdownArrow}>
            <img src={chevronDownIcon} alt="Dropdown arrow" />
          </span>
        </button>
      </div>

      {isOpen &&
        !disabled &&
        ReactDOM.createPortal(
          <div
            ref={dropdownListRef}
            style={menuStyle}
            className={s.dropdownList}
          >
            {showCustomInput ? (
              <div className={s.customInputContainer}>
                <p className={s.customInputLabel}>Enter your school name:</p>
                <input
                  type="text"
                  className={s.customInput}
                  value={customSchoolName}
                  onChange={(e) => setCustomSchoolName(e.target.value)}
                  placeholder="Your school name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCustomSchoolSubmit();
                    }
                  }}
                />
                <div className={s.customInputActions}>
                  <button
                    type="button"
                    className={s.customInputCancel}
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomSchoolName("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={s.customInputSubmit}
                    onClick={handleCustomSchoolSubmit}
                    disabled={!customSchoolName.trim()}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={s.searchContainer}>
                  <input
                    ref={inputRef}
                    type="text"
                    className={s.searchInput}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Type to search..."
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div className={s.optionsList}>
                  {loading && (
                    <div className={s.loadingState}>Searching...</div>
                  )}

                  {!loading && searchQuery.length >= 2 && schools.length === 0 && (
                    <div className={s.emptyState}>
                      No schools found. Select "Other" to enter manually.
                    </div>
                  )}

                  {!loading && searchQuery.length < 2 && (
                    <div className={s.hintState}>
                      Type at least 2 characters to search
                    </div>
                  )}

                  {!loading && schools.map((school) => (
                    <button
                      key={school.id}
                      type="button"
                      className={`${s.dropdownOption} ${
                        value?.school_id === school.id ? s.dropdownOptionSelected : ""
                      }`}
                      onClick={() => handleSelect(school)}
                    >
                      {school.name}
                    </button>
                  ))}

                  {/* Always show "Other" option when search has results or no results after search */}
                  {!loading && searchQuery.length >= 2 && (
                    <button
                      type="button"
                      className={`${s.dropdownOption} ${s.otherOption}`}
                      onClick={() => handleSelect({ name: "Other" })}
                    >
                      Other (enter manually)
                    </button>
                  )}
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default SchoolDropdown;
